import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchMe, logout as apiLogout, type SessionUser } from './api'

interface AuthCtx {
  user: SessionUser | null
  loading: boolean
  logout: () => Promise<void>
}

const Ctx = createContext<AuthCtx>({ user: null, loading: true, logout: async () => {} })

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: fetchMe,
    // No `enabled` gate: it was evaluated at render time, so a login that stored tokens after
    // the first render left the query disabled and invalidateQueries() never refetched it,
    // bouncing the user straight back to /login. fetchMe() already returns null without a
    // request when no token is stored.
    staleTime: 60_000,
    retry: false,
  })

  async function logout() {
    await apiLogout()
    qc.setQueryData(['session'], null)
    await qc.invalidateQueries({ queryKey: ['session'] })
  }

  return (
    <Ctx.Provider value={{ user: data ?? null, loading: isLoading, logout }}>
      {children}
    </Ctx.Provider>
  )
}

export const useAuth = () => useContext(Ctx)
